// Go Live from Browser — owner flow (web only).
// route: event/:slug/broadcast/:variant?  (variant: "live" | "test")
//
// The "test" variant streams to the user's free test event (same one the
// RTMP form uses), so the existing webhook drives putEventTestingLive /
// putEventLive exactly as with any external encoder.
import React, { useEffect, useState } from "react";
import { View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import tw from "twrnc";

import Text from "@/components/shared/AppText";
import AppTitle from "@/components/shared/AppTitle";
import OnboardingButton from "@/components/shared/OnboardingButton";
import EventSettingsScreenContainer from "@/components/EventSettingsScreenContainer";
import BrowserStreamStudio from "@/components/streaming/BrowserStreamStudio";
import { isBrowserStreamSupported } from "@/components/streaming/capabilities";
import {
  desktopStudioPlatform,
  hasDesktopStudio,
} from "@/components/desktop/desktopApp";
import { openStreamWindow } from "@/components/Utils";
import useSubscribeToEventUpdates from "@/components/hooks/useSubscribeToEventUpdates";
import { useAuth } from "@/components/AuthContext";
import useWorkspaceAuth, {
  useEventAuth as useEventAuthFor,
} from "@/components/teams/useWorkspaceAuth";
import { createTest } from "@/components/http/events";
import { makeUrl } from "@/components/globalUtils";
import useNow from "@/components/hooks/useNow";
import GoLiveWindowNotice, {
  goLiveWindow,
} from "@/components/streaming/GoLiveWindowNotice";
import { t } from "@/localization";

const BrowserStream = ({ route }) => {
  const { slug, variant } = route?.params || {};
  const isTest = variant === "test";
  const navigation = useNavigation();
  // Teams: the event is in the account you work in; your test stays yours.
  const { broadcaster } = useWorkspaceAuth();
  const me = useAuth();

  const { event, loading } = useSubscribeToEventUpdates(
    broadcaster && slug ? { broadcaster, slug } : null,
    Boolean(broadcaster && slug)
  );

  const [testEvent, setTestEvent] = useState(null);
  useEffect(() => {
    if (isTest && me.broadcaster) {
      createTest(me.broadcaster, false).then(setTestEvent).catch(() => {});
    }
  }, [isTest, me.broadcaster]);
  // Live status/viewers for the test event come from its own subscription.
  const { event: liveTestEvent } = useSubscribeToEventUpdates(
    testEvent
      ? { broadcaster: testEvent.broadcaster, slug: testEvent.slug }
      : null,
    Boolean(testEvent)
  );

  const activeEvent = isTest ? liveTestEvent || testEvent : event;
  // The custom domain of the account the streamed event is in.
  const { custom_domain } = useEventAuthFor(activeEvent);
  const watchUrl = activeEvent ? makeUrl(activeEvent, custom_domain) : "";

  const needsPurchase =
    !isTest && event && !event.is_published && !event.is_test;
  const ready = !loading && activeEvent?.stream_key;
  // Outside the go-live window the server refuses the stream: say when it opens.
  const now = useNow();
  const liveWindow = isTest ? { open: true } : goLiveWindow(event, now);

  return (
    <EventSettingsScreenContainer loading={loading && !activeEvent}>
      <View style={tw`w-full max-w-[720px] self-center px-4`}>
        <AppTitle
          text={activeEvent?.title || (isTest ? t("Free test") : t("Go Live"))}
          style={tw`text-left mb-1`}
        />
      </View>

      {hasDesktopStudio() && ready && !needsPurchase && (
        <View style={tw`w-full max-w-[720px] self-center px-4 py-4`}>
          <Text style={tw`mb-4`}>
            {desktopStudioPlatform() === "windows"
              ? t(
                  "EventLive for Windows streams from its Studio window, with seamless camera and microphone switching."
                )
              : t(
                  "EventLive for Mac streams from its Studio window, with seamless camera and microphone switching."
                )}
          </Text>
          <OnboardingButton
            text={t("Open Studio")}
            icon="videocam-outline"
            onPress={() => openStreamWindow(activeEvent)}
          />
        </View>
      )}

      {!hasDesktopStudio() && !isBrowserStreamSupported() && (
        <View style={tw`w-full max-w-[720px] self-center px-4 py-4`}>
          <Text>
            {t(
              "This browser can't stream directly. Use a recent version of Chrome, Edge or Safari — or go live with the EventLive app."
            )}
          </Text>
        </View>
      )}

      {(hasDesktopStudio() || isBrowserStreamSupported()) && needsPurchase && (
        <View style={tw`w-full max-w-[720px] self-center px-4 py-4`}>
          <Text style={tw`mb-4`}>
            {t(
              "This event isn't unlocked yet. Unlock it to go live, or run a free test."
            )}
          </Text>
          <OnboardingButton
            text={t("Unlock event")}
            onPress={() =>
              navigation.navigate("PayWall", { event, slug: event.slug })
            }
          />
          <OnboardingButton
            text={t("Start free test in browser")}
            bordered
            dark
            style={tw`mt-3`}
            onPress={() =>
              navigation.replace("BrowserStream", { slug, variant: "test" })
            }
          />
        </View>
      )}

      {!hasDesktopStudio() &&
        isBrowserStreamSupported() &&
        !needsPurchase &&
        ready &&
        !liveWindow.open && (
          <View style={tw`w-full max-w-[720px] self-center px-4`}>
            <GoLiveWindowNotice
              event={event}
              window={liveWindow}
              onTest={() =>
                navigation.replace("BrowserStream", { slug, variant: "test" })
              }
              onChangeDate={() =>
                navigation.navigate("EventDetails", { slug: event.slug, event })
              }
            />
          </View>
        )}

      {!hasDesktopStudio() &&
        isBrowserStreamSupported() &&
        !needsPurchase &&
        ready &&
        liveWindow.open && (
          <BrowserStreamStudio
            event={activeEvent}
            streamKey={activeEvent.stream_key}
            isTest={isTest || Boolean(activeEvent?.is_test)}
            watchUrl={watchUrl}
          />
        )}
    </EventSettingsScreenContainer>
  );
};

export default BrowserStream;
